/**
 * machineDeactivationPrompts.ts
 * Pure ConfirmDialogState builders for the machine retirement flow
 * (G2-10, audit #2). Extracted from MachinesPage.tsx (600-line cap,
 * CODING-STANDARDS).
 *
 * Two-phase flow:
 *   1. DELETE refused (LIVE_BOOKING_GUARD) → "Deactivate instead?" offer.
 *   2. Deactivate refused (FUTURE_BOOKINGS_CONFIRM) → relocation prompt
 *      listing the machine's future bookings; confirm keeps them and retires
 *      the machine (advisory, never blocks).
 */

import type {
  DeactivateFutureBooking,
  DeactivateNeedsConfirmError,
  MachineRow,
} from '../../../services/scheduleMachines';
import type { ConfirmDialogState } from '../../../components/shared/ConfirmDialog';

/** G2-10: short label for one future booking in the relocation prompt. */
export function futureBookingLabel(b: DeactivateFutureBooking): string {
  const what =
    b.TaskName ??
    b.EventType ??
    (b.QuoteNumber ? `Quote ${b.QuoteNumber}` : b.WorkOrderId ? `WO ${b.WorkOrderId}` : 'Booking');
  const span = b.StartDate === b.EndDate ? b.StartDate : `${b.StartDate} → ${b.EndDate}`;
  return `${what} (${b.Status}, ${span})`;
}

/**
 * G2-10: relocation prompt state — the deactivate call answered 409
 * needsConfirm with the machine's future bookings; show the count + the first
 * few labelled rows and offer "keep them & deactivate".
 */
export function buildRelocationPromptState(
  machine: MachineRow,
  err: DeactivateNeedsConfirmError,
  onConfirm: () => void,
): ConfirmDialogState {
  const preview = err.futureBookings.slice(0, 5).map((b) => `• ${futureBookingLabel(b)}`);
  if (err.futureBookingCount > preview.length) {
    preview.push(`• …and ${err.futureBookingCount - preview.length} more`);
  }
  const plural = err.futureBookingCount !== 1 ? 's' : '';
  return {
    open: true,
    title: `${err.futureBookingCount} Future Booking${plural} on This Machine`,
    message:
      `"${machine.Code} – ${machine.Name}" still has ${err.futureBookingCount} future booking` +
      `${plural}:\n\n${preview.join('\n')}\n\n` +
      `Reassign the work to another machine first (drag the bars on the Machine Schedule), ` +
      `or deactivate now and keep the bookings where they are — the machine stays visible ` +
      `on the schedule as a decommissioned row until the work is moved.`,
    confirmLabel: 'Deactivate & Keep Bookings',
    confirmColor: 'warning',
    onConfirm,
  };
}

/**
 * Phase 5.7 "deactivate instead" offer state — shown when DELETE is refused
 * with LIVE_BOOKING_GUARD.
 */
export function buildDeactivateOfferState(
  machine: MachineRow,
  refusalMessage: string,
  onConfirm: () => void,
): ConfirmDialogState {
  return {
    open: true,
    title: 'Deactivate Machine Instead?',
    message:
      `${refusalMessage} ` +
      `Deactivating "${machine.Code} – ${machine.Name}" will retire it from new scheduling pickers ` +
      `while preserving all booking history.`,
    confirmLabel: 'Deactivate Machine',
    confirmColor: 'warning',
    onConfirm,
  };
}
